import React, { useEffect, useState } from 'react'
import {
  createWorkerDocumentConsent,
  listWorkerDocumentConsentsForWorker,
  updateWorkerDocumentConsent,
} from '../../../services/access'
import type {
  Organization,
  WorkerDocumentConsent,
  WorkerDocumentConsentScope,
  WorkerDocumentConsentStatus,
} from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { ConfirmDialog, EmptyState, EntityActionMenu, ErrorBanner, FormModal, LoadingState } from './AdminUiPrimitives'

interface WorkerDocumentConsentSectionProps {
  workerProfileId: string
  workerName: string
  organizations: Organization[]
  selectedOrgId: string
  canManageDocumentConsent: boolean
}

const consentScopes: WorkerDocumentConsentScope[] = ['all_documents', 'selected_documents', 'credential_summary']
const managerStatusOptions: WorkerDocumentConsentStatus[] = ['pending', 'expired']
const allStatusOptions: WorkerDocumentConsentStatus[] = ['pending', 'granted', 'revoked', 'expired']
const datetimePayloadValue = (value: string) => value ? new Date(value).toISOString() : null
const readable = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase())
const scopeLabel = (scope: string, t: (text: string) => string) => t(readable(scope))
const statusLabel = (status: string, t: (text: string) => string) => t(readable(status))

/**
 * Per-worker document-consent management. Rendered inside the selected
 * worker's detail view (Compliance & Documents tab) so consent requests are
 * always scoped to one worker and the currently selected organization —
 * never a page-level cross-organization list. Managers may only request
 * consent or expire a request; grant/revoke stays worker-owned and is
 * enforced again by the backend.
 */
export const WorkerDocumentConsentSection: React.FC<WorkerDocumentConsentSectionProps> = ({
  workerProfileId,
  workerName,
  organizations,
  selectedOrgId,
  canManageDocumentConsent,
}) => {
  const { t, formatDate } = useI18n()
  const [consents, setConsents] = useState<WorkerDocumentConsent[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [reviewConsent, setReviewConsent] = useState<WorkerDocumentConsent | null>(null)
  const [confirmConsent, setConfirmConsent] = useState<WorkerDocumentConsent | null>(null)
  const [form, setForm] = useState({
    consentScope: 'all_documents' as WorkerDocumentConsentScope,
    startsAt: '',
    endsAt: '',
  })
  const [reviewForm, setReviewForm] = useState({
    status: 'pending' as WorkerDocumentConsentStatus,
    startsAt: '',
    endsAt: '',
    notes: '',
  })

  useEffect(() => {
    if (!workerProfileId || !selectedOrgId || !canManageDocumentConsent) {
      setConsents([])
      return
    }
    let mounted = true
    setLoading(true)
    setError('')
    listWorkerDocumentConsentsForWorker(workerProfileId, selectedOrgId)
      .then((rows) => mounted && setConsents(rows))
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load document consents for this worker.')))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [canManageDocumentConsent, selectedOrgId, t, workerProfileId])

  const refresh = async () => {
    if (!workerProfileId || !selectedOrgId || !canManageDocumentConsent) return
    const rows = await listWorkerDocumentConsentsForWorker(workerProfileId, selectedOrgId)
    setConsents(rows)
  }

  const openReview = (consent: WorkerDocumentConsent) => {
    setReviewConsent(consent)
    setReviewForm({
      status: managerStatusOptions.includes(consent.status) ? consent.status : 'pending',
      startsAt: consent.starts_at ? consent.starts_at.slice(0, 16) : '',
      endsAt: consent.ends_at ? consent.ends_at.slice(0, 16) : '',
      notes: String(consent.metadata?.managerNotes || ''),
    })
    setMessage('')
    setError('')
  }

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageDocumentConsent || !workerProfileId || !selectedOrgId) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await createWorkerDocumentConsent(workerProfileId, {
        orgId: selectedOrgId,
        consentScope: form.consentScope,
        status: 'pending',
        startsAt: datetimePayloadValue(form.startsAt),
        endsAt: datetimePayloadValue(form.endsAt),
      })
      await refresh()
      setForm({ consentScope: 'all_documents', startsAt: '', endsAt: '' })
      setShowCreateModal(false)
      setMessage(t('Document consent request created. The worker decides whether to grant it.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to create worker document consent.'))
    } finally {
      setSaving(false)
    }
  }

  const handleReviewSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!reviewConsent) return
    if (!managerStatusOptions.includes(reviewForm.status)) {
      setError(t('Managers can only set consent requests to pending or expired. Worker-owned grant/revoke remains backend enforced.'))
      return
    }
    setConfirmConsent(reviewConsent)
  }

  const handleUpdate = async () => {
    if (!reviewConsent || !confirmConsent || !canManageDocumentConsent) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await updateWorkerDocumentConsent(confirmConsent.id, {
        status: reviewForm.status,
        startsAt: datetimePayloadValue(reviewForm.startsAt),
        endsAt: datetimePayloadValue(reviewForm.endsAt),
        metadata: {
          ...(confirmConsent.metadata || {}),
          managerNotes: reviewForm.notes.trim() || null,
          managerReviewedAt: new Date().toISOString(),
        },
      })
      await refresh()
      setReviewConsent(null)
      setConfirmConsent(null)
      setMessage(t('Document consent updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update worker document consent.'))
    } finally {
      setSaving(false)
    }
  }

  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)

  return (
    <div className="stack" data-testid="worker-document-consent-section">
      <div className="admin-panel-toolbar">
        <div>
          <h4 style={{ margin: 0 }}>{t('Documents & consent')}</h4>
          <p className="helper">{t('Document consent controls whether {worker} allows {org} to access specific document metadata or files. Consent is worker-controlled and does not change who owns the document or the worker.', { worker: workerName, org: selectedOrg?.name || t('the selected organization') })}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="request-worker-consent-btn"
          disabled={!canManageDocumentConsent || !selectedOrgId || saving}
          onClick={() => setShowCreateModal(true)}
        >{t('Request document consent')}</button>
      </div>
      {!canManageDocumentConsent ? (
        <p className="admin-disabled-callout">{t('Requesting document consent requires worker document sharing or worker management permission for this organization.')}</p>
      ) : null}

      {message ? <p className="helper">{message}</p> : null}
      <ErrorBanner message={error} />

      <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
        {loading ? <LoadingState label={t('Loading consent records...')} /> : (
          <>
            <table className="workspace-table">
              <thead><tr><th>{t('Scope')}</th><th>{t('Status')}</th><th>{t('Starts')}</th><th>{t('Ends')}</th><th>{t('Granted')}</th><th>{t('Revoked')}</th><th>{t('Actions')}</th></tr></thead>
              <tbody>
                {consents.map((consent) => (
                  <tr key={consent.id}>
                    <td>{scopeLabel(consent.consent_scope, t)}</td>
                    <td><span className="status-pill">{statusLabel(consent.status, t)}</span></td>
                    <td>{formatDate(consent.starts_at)}</td>
                    <td>{formatDate(consent.ends_at)}</td>
                    <td>{formatDate(consent.granted_at)}</td>
                    <td>{formatDate(consent.revoked_at)}</td>
                    <td>
                      <EntityActionMenu>
                        <button type="button" className="btn btn-ghost" disabled={!canManageDocumentConsent} onClick={() => openReview(consent)}>{t('Review consent')}</button>
                      </EntityActionMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {consents.length === 0 ? <EmptyState title={t('No document consent records')} description={t('Request document-sharing consent for this worker in the selected organization scope.')} /> : null}
          </>
        )}
      </div>
      <p className="helper">{t('Managers can request consent or expire a request. Grant/revoke remains worker-owned where backend enforcement applies, and consent controls sharing between organizations, not compliance requirements.')}</p>

      <FormModal
        title={t('Request document consent')}
        description={t('{worker} / {org}', { worker: workerName, org: selectedOrg?.name || t('Selected organization') })}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setShowCreateModal(false)}>{t('Cancel')}</button>
            <button type="submit" form="worker-consent-create-form" className="btn btn-primary" disabled={!canManageDocumentConsent || saving}>{saving ? t('Saving...') : t('Request consent')}</button>
          </>
        )}
      >
        <form id="worker-consent-create-form" className="admin-form-grid" onSubmit={handleCreate}>
          <div className="form-row">
            <label htmlFor="worker-consent-scope">{t('Scope')}</label>
            <select id="worker-consent-scope" className="input" value={form.consentScope} onChange={(event) => setForm({ ...form, consentScope: event.target.value as WorkerDocumentConsentScope })} disabled={!canManageDocumentConsent}>
              {consentScopes.map((scope) => <option key={scope} value={scope}>{scopeLabel(scope, t)}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="worker-consent-starts">{t('Starts at')}</label>
            <input id="worker-consent-starts" type="datetime-local" className="input" value={form.startsAt} onChange={(event) => setForm({ ...form, startsAt: event.target.value })} disabled={!canManageDocumentConsent} />
          </div>
          <div className="form-row">
            <label htmlFor="worker-consent-ends">{t('Ends at')}</label>
            <input id="worker-consent-ends" type="datetime-local" className="input" value={form.endsAt} onChange={(event) => setForm({ ...form, endsAt: event.target.value })} disabled={!canManageDocumentConsent} />
          </div>
          <p className="helper admin-form-wide">{t('The request is created as pending. Only the worker can grant or revoke it.')}</p>
        </form>
      </FormModal>

      <FormModal
        title={t('Review document consent')}
        description={reviewConsent ? `${workerName} - ${scopeLabel(reviewConsent.consent_scope, t)}` : undefined}
        isOpen={Boolean(reviewConsent)}
        onClose={() => setReviewConsent(null)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setReviewConsent(null)}>{t('Cancel')}</button>
            <button type="submit" form="worker-consent-review-form" className="btn btn-primary" disabled={!canManageDocumentConsent || saving}>{saving ? t('Saving...') : t('Continue')}</button>
          </>
        )}
      >
        <form id="worker-consent-review-form" className="stack" onSubmit={handleReviewSubmit}>
          <div className="admin-form-grid">
            <div className="form-row">
              <label>{t('Status')}</label>
              <select className="input" value={reviewForm.status} onChange={(event) => setReviewForm({ ...reviewForm, status: event.target.value as WorkerDocumentConsentStatus })}>
                {allStatusOptions.map((status) => <option key={status} value={status} disabled={!managerStatusOptions.includes(status)}>{statusLabel(status, t)}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>{t('Starts at')}</label>
              <input type="datetime-local" className="input" value={reviewForm.startsAt} onChange={(event) => setReviewForm({ ...reviewForm, startsAt: event.target.value })} />
            </div>
            <div className="form-row">
              <label>{t('Ends at')}</label>
              <input type="datetime-local" className="input" value={reviewForm.endsAt} onChange={(event) => setReviewForm({ ...reviewForm, endsAt: event.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <label>{t('Notes')}</label>
            <textarea className="input" rows={4} value={reviewForm.notes} onChange={(event) => setReviewForm({ ...reviewForm, notes: event.target.value })} />
          </div>
        </form>
      </FormModal>

      <ConfirmDialog
        title={t('Update document consent?')}
        message={confirmConsent ? t('{title} will be marked {status}. Worker-owned grant/revoke transitions remain unavailable here.', { title: `${workerName} - ${scopeLabel(confirmConsent.consent_scope, t)}`, status: statusLabel(reviewForm.status, t) }) : ''}
        isOpen={Boolean(confirmConsent)}
        confirmLabel={t('Update')}
        disabled={saving}
        onCancel={() => setConfirmConsent(null)}
        onConfirm={handleUpdate}
      />
    </div>
  )
}
